import { useApolloClient } from "@apollo/client";
import { Check as CheckIcon } from "@mui/icons-material";
import { Button, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { LoginButton } from "../auth/LoginButton.js";
import { RegisterButton } from "../auth/RegisterButton.js";
import { INVITATION_SCOPE } from "../auth/signup/signupScope.js";
import { AvailableUser, SelectUserList } from "../auth/SelectUserList.js";
import { useSelectedUser } from "../auth/useSelectedUser.js";
import { SELECTED_USER_HTTP_HEADER } from "../constants.js";
import { OrganisationAvatar } from "../features/organisations/OrganisationAvatar.js";
import {
  ONBOARDING_BUTTON_HEIGHT,
  ONBOARDING_BUTTON_LABEL,
  ONBOARDING_OUTLINED_BUTTON_SX,
  ONBOARDING_PRIMARY_BUTTON_SX,
} from "../features/organisations/onboarding/onboardingDesign.js";
import { ButtonDivider } from "./ButtonDivider.js";
import { InvitationHeading } from "./InvitationHeading.js";
import { type RefTokenQuery } from "./InvitationTokenProvider.generated.js";
import { useAcceptProjectContracteeInvitationMutation } from "./ProjectContracteeInvitation.generated.js";
import { useInvitationToken } from "./useInvitationToken.js";

type Props = {
  invitation: Extract<
    RefTokenQuery["projectContracteeInvitation"],
    { __typename: "ProjectContracteeInvitation" }
  >;
} & (
  | {
      isAuthenticated: false;
      availableUsers?: void;
      availableUsersIsLoading?: void;
    }
  | {
      isAuthenticated: true;
      availableUsers: AvailableUser[];
      availableUsersIsLoading: boolean;
    }
);

export const ProjectContracteeInvitation = ({
  invitation,
  isAuthenticated,
  availableUsers,
  availableUsersIsLoading,
}: Props) => {
  const { t } = useTranslate(["Global"]);
  const { setSelectedUserId } = useSelectedUser();
  const { remove: removeInvitationToken } = useInvitationToken();

  const [selectedUser, setSelectedUser] = React.useState<AvailableUser | null>(
    null
  );

  const client = useApolloClient();
  const [acceptProjectContracteeInvitation] =
    useAcceptProjectContracteeInvitationMutation({ client });

  if (!isAuthenticated) {
    return (
      <ProjectContracteeInvitationUnAuthenticated invitation={invitation} />
    );
  }

  return (
    <Stack sx={{ alignItems: "center" }}>
      <OrganisationAvatar
        organisationAvatar={invitation.author.company}
        size="xl"
      />
      <InvitationHeading sx={{ mt: "24px" }}>
        {t(
          "Please choose the organisation that will work with {inviterOrgName}",
          {
            ns: "Global",
            inviterOrgName: invitation.author.company.title,
          }
        )}
      </InvitationHeading>
      <Stack sx={{ width: "100%", mt: "24px" }}>
        <SelectUserList
          isLoading={availableUsersIsLoading}
          availableUsers={availableUsers}
          selectedUser={selectedUser}
          handleSelect={setSelectedUser}
        />
      </Stack>
      <Stack sx={{ width: "100%", mt: "24px" }}>
        <Button
          fullWidth
          sx={ONBOARDING_PRIMARY_BUTTON_SX}
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<CheckIcon />}
          onClick={async () => {
            if (!selectedUser) throw new Error("Need to select user first!");
            await acceptProjectContracteeInvitation({
              variables: {
                input: { invitationToken: invitation.invitationToken },
              },
              context: {
                headers: {
                  [SELECTED_USER_HTTP_HEADER]: selectedUser.id,
                },
              },
            });
            removeInvitationToken();
            setSelectedUserId(selectedUser.id);
          }}
          disabled={!selectedUser}
        >
          {t("Select", { ns: "Global" })}
        </Button>
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <Button
          fullWidth
          variant="outlined"
          color="warning"
          sx={{
            height: ONBOARDING_BUTTON_HEIGHT,
            px: "22px",
            ...ONBOARDING_BUTTON_LABEL,
          }}
          onClick={() => {
            removeInvitationToken();
          }}
        >
          {t("Discard", { ns: "Global" })}
        </Button>
      </Stack>
    </Stack>
  );
};

const ProjectContracteeInvitationUnAuthenticated = ({
  invitation,
}: Pick<Props, "invitation">) => {
  const { t } = useTranslate(["Global"]);

  const prefill = React.useMemo(() => {
    const firstName = invitation.contact.firstname;
    const lastName = invitation.contact.familyname;
    const email = invitation.contact.email;
    const salutation = invitation.contact.title;

    return { firstName, lastName, email, salutation };
  }, [
    invitation.contact.email,
    invitation.contact.familyname,
    invitation.contact.firstname,
    invitation.contact.title,
  ]);

  return (
    <Stack sx={{ alignItems: "center" }}>
      <OrganisationAvatar
        organisationAvatar={invitation.author.company}
        size="xl"
      />
      <InvitationHeading sx={{ mt: "24px" }}>
        {t(
          "Please sign in to collaborate with {inviterOrgName} on Meister Systems",
          {
            ns: "Global",
            inviterOrgName: invitation.author.company.title,
          }
        )}
      </InvitationHeading>
      <Stack sx={{ width: "100%", mt: "32px" }}>
        <RegisterButton
          prefill={prefill}
          organisationType="CLIENT"
          scope={INVITATION_SCOPE}
          color="primary"
          sx={ONBOARDING_PRIMARY_BUTTON_SX}
        />
        <ButtonDivider label={t("Or", { ns: "Global" })} />
        <LoginButton
          color="primary"
          variant="outlined"
          prefill={prefill}
          scope={INVITATION_SCOPE}
          sx={ONBOARDING_OUTLINED_BUTTON_SX}
        />
      </Stack>
    </Stack>
  );
};
